import React from 'react';
import { Factory, Fuel, Building2, Zap, Hammer } from 'lucide-react';
import { industries } from '../mock';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useLanguage } from '../i18n/LanguageContext';
import { Link } from 'react-router-dom';

// Customer/Partner logos data
const customerLogos = [
  { name: 'Trisula Textile Industry', logo: '/assets/partners/trisulatex.png' },
  { name: 'Dhanar Mas Concern', logo: '/assets/partners/dhanarmas.png' },
  { name: 'Indofood CBP Sukses Makmur', logo: '/assets/partners/indofood.png' },
  { name: 'Daliatex Kusuma', logo: '/assets/partners/daliatex.png' },
  { name: 'Feng Tay Indonesia Enterprise', logo: '/assets/partners/fengtay.png' },
  { name: 'Indo Pacific', logo: '/assets/partners/ip.png' },
  { name: 'Nirwana Group', logo: '/assets/partners/nirwana.png' },
  { name: 'Nishikawa Karya Indonesia', logo: '/assets/partners/nishikawa.png' },
  { name: 'Triputra Textile Industry', logo: '/assets/partners/triputra.png' },
  { name: 'Akurasi Konstruksi Indonesia', logo: '/assets/partners/aki.png' },
  { name: 'Bunyamin Inovasi Teknik', logo: '/assets/partners/bunyamin.png' },
  { name: 'Duta Solusi Metalindo', logo: '/assets/partners/dutasolusi.png' },
  { name: 'Sanbe Farma', logo: '/assets/partners/sanbe.png' },
];

const Industries = () => {
  const { t, language } = useLanguage();

  const industryIcons = {
    1: Factory,
    2: Fuel,
    3: Building2,
    4: Hammer,
    5: Zap
  };

  const industryTranslations = [
    { key: 'manufacturing', nameKey: 'industryNames.manufacturing', descKey: 'industryNames.manufacturingDesc' },
    { key: 'oilGas', nameKey: 'industryNames.oilGas', descKey: 'industryNames.oilGasDesc' },
    { key: 'construction', nameKey: 'industryNames.construction', descKey: 'industryNames.constructionDesc' },
    { key: 'infrastructure', nameKey: 'industryNames.infrastructure', descKey: 'industryNames.infrastructureDesc' },
    { key: 'powerUtilities', nameKey: 'industryNames.powerUtilities', descKey: 'industryNames.powerUtilitiesDesc' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              {t('industries.pageTitle')}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('industries.pageSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => {
              const IconComponent = industryIcons[industry.id];
              const translation = industryTranslations[index];
              return (
                <Card key={industry.id} className="p-8 hover:shadow-xl transition-all hover:-translate-y-2 duration-300 border-t-4 border-[#0693E3]">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
                    <IconComponent className="text-[#0693E3]" size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{t(translation.nameKey)}</h3>
                  <p className="text-gray-600 leading-relaxed">{t(translation.descKey)}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Customer Logos Marquee Section */}
      <section className="py-16 bg-gray-50 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              {language === 'id' ? 'Customer Kami' : 'Our Customers'}
            </h2>
            <p className="text-gray-600">
              {language === 'id'
                ? 'Dipercaya oleh perusahaan-perusahaan terkemuka di Indonesia'
                : 'Trusted by leading companies in Indonesia'}
            </p>
          </div>
        </div>

        {/* Marquee Container */}
        <div className="relative">
          {/* Gradient overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-gray-50 to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-gray-50 to-transparent z-10" />

          {/* Scrolling logos - Row 1 */}
          <div className="flex w-max animate-marquee">
            {[...customerLogos, ...customerLogos].map((customer, index) => (
              <div
                key={index}
                className="flex-shrink-0 mx-8 flex items-center justify-center bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                style={{ minWidth: '180px', height: '100px' }}
              >
                <img
                  src={customer.logo}
                  alt={customer.name}
                  className="max-h-12 max-w-[140px] object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<span class="text-gray-700 font-semibold text-sm">${customer.name}</span>`;
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* CSS Animation */}
        <style>{`
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee {
            animation: marquee 30s linear infinite;
          }
          .animate-marquee:hover {
            animation-play-state: paused;
          }
        `}</style>
      </section>

      {/* Featured Projects Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('industries.applicationsTitle')}</h2>
            <p className="text-xl text-gray-600">{t('industries.applicationsSubtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="overflow-hidden hover:shadow-xl transition-all duration-300">
              <img
                src="https://images.unsplash.com/photo-1647427060118-4911c9821b82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwbWFudWZhY3R1cmluZ3xlbnwwfHx8fDE3NjU4NjAwOTV8MA&ixlib=rb-4.1.0&q=85"
                alt="Automated Manufacturing"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('industries.automatedProduction')}</h3>
                <p className="text-gray-600">{t('industries.automatedProductionDesc')}</p>
              </div>
            </Card>

            <Card className="overflow-hidden hover:shadow-xl transition-all duration-300">
              <img
                src="https://images.unsplash.com/photo-1455165814004-1126a7199f9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwbWFudWZhY3R1cmluZ3xlbnwwfHx8fDE3NjU4NjAwOTV8MA&ixlib=rb-4.1.0&q=85"
                alt="Industrial Fabrication"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('industries.heavyIndustry')}</h3>
                <p className="text-gray-600">{t('industries.heavyIndustryDesc')}</p>
              </div>
            </Card>

            <Card className="overflow-hidden hover:shadow-xl transition-all duration-300">
              <img
                src="https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwbWFudWZhY3R1cmluZ3xlbnwwfHx8fDE3NjU4NjAwOTV8MA&ixlib=rb-4.1.0&q=85"
                alt="Process Control"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('industries.processControl')}</h3>
                <p className="text-gray-600">{t('industries.processControlDesc')}</p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0693E3]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {t('industries.ctaTitle')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('industries.ctaSubtitle')}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#CF2E2E] hover:bg-[#B02525] text-white">
              {t('industries.scheduleConsultation')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Industries;
